import { useState, type ReactElement } from 'react';
import { Check, Copy, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { S3ConfigViewModel } from '../api';
import { S3ConfigStatusBadge } from './S3ConfigStatusBadge';

export function S3ConfigsTable({
  rows,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly rows: readonly S3ConfigViewModel[];
  readonly onEdit: (row: S3ConfigViewModel) => void;
  readonly onDelete: (row: S3ConfigViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.s3.configs.table';

  return (
    <DataTableTable aria-label={t('routes.admin.s3.configs.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.name`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.bucket`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[8rem]">
            {t(`${col}.region`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.folder`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[7rem]">
            {t(`${col}.status`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[6rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <DataTableTr key={row.id} className="even:bg-muted/55">
            <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
              <span className="inline-flex items-center gap-2">
                {row.name}
                {row.isDefault && <Badge variant="default">{t(`${col}.defaultBadge`)}</Badge>}
                {!row.active && <Badge variant="outline">{t(`${col}.inactiveBadge`)}</Badge>}
              </span>
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
              <CopyableValue value={row.bucketName} />
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
              {row.region}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
              {row.folder.length > 0 ? (
                row.folder
              ) : (
                <span className="italic text-muted-foreground/70">{t(`${col}.folderTenant`)}</span>
              )}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0" dataOffset={false}>
              <S3ConfigStatusBadge active={row.active} />
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0" dataOffset={false}>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.editAction`)}
                  title={t(`${col}.editAction`)}
                  onClick={() => {
                    onEdit(row);
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.deleteAction`)}
                  title={t(`${col}.deleteAction`)}
                  loading={pendingDeleteId === row.id}
                  onClick={() => {
                    onDelete(row);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

/** A monospace value with a hover copy-to-clipboard button. Empty values render nothing. */
function CopyableValue({ value }: { readonly value: string }): ReactElement | null {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (value.length === 0) return null;

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      {value}
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        aria-label={t('routes.admin.s3.configs.table.copyBucket')}
        title={t('routes.admin.s3.configs.table.copyBucket')}
        onClick={() => {
          void handleCopy();
        }}
      >
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
      </Button>
    </span>
  );
}
